import { Fragment, useMemo, useState } from "react";
import { WorkOrdersFieldsFragment } from "@/gql/graphql";
import { Marker, Polyline, Tooltip } from "react-leaflet";
import L from "leaflet";
import dayjs from "dayjs";
import { Avatar } from "@/components/catalyst/avatar";
import { Button } from "@/components/catalyst/button";
import { PencilIcon } from "@heroicons/react/20/solid";
import { Map } from "@/components/ui/map";
import { TimezoneSelect } from "@/components/ui/timezone-select";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { assignmentLocationsQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderAssignmentLocationsQuery";
import { getInitials } from "@/lib/Utils/getInitials";
import { BasemapLayer } from "@/components/map/BasemapLayer";
import { FitBounds } from "@/components/map/FitBounds";

const TRACK_COLORS = [
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#0ea5e9",
  "#84cc16",
];

const FIFTEEN_MINUTES = 15 * 60 * 1000;

function buildBadge(
  text: string,
  color: string,
  size: number,
  fontSize: number,
  fontWeight: string,
): HTMLDivElement {
  const el = document.createElement("div");
  el.style.cssText = `background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: ${fontWeight}; font-size: ${fontSize}px; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.4);`;
  el.textContent = text;
  return el;
}

function createAvatarIcon(
  name: string | null | undefined,
  color: string,
  pictureUrl: string | null | undefined,
) {
  const initials = name ? getInitials(name).toUpperCase() : "?";
  const badge = buildBadge(pictureUrl ? "" : initials, color, 36, 13, "600");
  if (pictureUrl) {
    badge.style.backgroundImage = `url("${pictureUrl}")`;
    badge.style.backgroundSize = "cover";
    badge.style.backgroundPosition = "center";
    badge.style.borderColor = color;
  }
  return L.divIcon({
    html: badge,
    className: "",
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function createCustomIcon(label: string, color: string) {
  return L.divIcon({
    html: buildBadge(label, color, 30, 14, "bold"),
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

function toCoords(
  lat: number | null | undefined,
  lng: number | null | undefined,
): [number, number] | null {
  return lat && lng ? [lat, lng] : null;
}

function floorToQuarterHour(ms: number, timezone: string) {
  const d = dayjs(ms).tz(timezone);
  return d
    .minute(d.minute() - (d.minute() % 15))
    .second(0)
    .millisecond(0)
    .valueOf();
}

function ceilToQuarterHour(ms: number, timezone: string) {
  const floored = floorToQuarterHour(ms, timezone);
  return floored === ms ? ms : floored + FIFTEEN_MINUTES;
}

const center: [number, number] = [-25.2744, 133.7751];

const mapPadding: [number, number] = [40, 40];

interface WorkOrderMapProps {
  workOrder: WorkOrdersFieldsFragment;
  onEdit?: () => void;
  showEditButton?: boolean;
}

export function WorkOrderMap({
  workOrder,
  onEdit,
  showEditButton = true,
}: WorkOrderMapProps) {
  const assignments = workOrder.assignments;
  const hasAnyLocation = assignments.some((a) => a.latestLocation);

  const { data: locationsData } = useGqlQuery(
    assignmentLocationsQuery,
    { assignmentIds: assignments.map((a) => a.id) },
    { enabled: hasAnyLocation, refetchInterval: 30000 },
  );
  const tracks = locationsData?.assignmentLocations ?? [];

  const [hiddenAssignmentIds, setHiddenAssignmentIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [timezone, setTimezone] = useState(() => dayjs.tz.guess());

  function toggleAssignment(id: string) {
    setHiddenAssignmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const colorByAssignment: Record<string, string> = {};
  const driverNameById: Record<string, string | null | undefined> = {};
  const pictureUrlById: Record<string, string | null | undefined> = {};
  assignments.forEach((a, index) => {
    colorByAssignment[a.id] = TRACK_COLORS[index % TRACK_COLORS.length];
    driverNameById[a.id] = a.proDriver?.name;
    pictureUrlById[a.id] = a.proDriver?.user?.profilePicture?.url;
  });

  const { lat: departureLat, lng: departureLng } = workOrder.departureAddress;
  const { lat: deliveryLat, lng: deliveryLng } = workOrder.deliveryAddress;

  const departureCoords = toCoords(departureLat, departureLng);
  const deliveryCoords = toCoords(deliveryLat, deliveryLng);

  const latestLocationsKey = assignments
    .map((a) =>
      a.latestLocation
        ? `${a.latestLocation.latitude},${a.latestLocation.longitude}`
        : "",
    )
    .join("|");

  const bounds = useMemo(() => {
    const points = [
      toCoords(departureLat, departureLng),
      toCoords(deliveryLat, deliveryLng),
      ...latestLocationsKey
        .split("|")
        .filter(Boolean)
        .map((pair) => pair.split(",").map(Number) as [number, number]),
    ].filter((point) => point !== null);

    return points.length > 0 ? L.latLngBounds(points) : null;
  }, [
    departureLat,
    departureLng,
    deliveryLat,
    deliveryLng,
    latestLocationsKey,
  ]);

  const timeBounds = useMemo(() => {
    const timestamps = tracks.flatMap((t) =>
      t.locations.map((loc) => dayjs.utc(loc.recorded_at).valueOf()),
    );
    if (timestamps.length === 0) {
      return null;
    }
    return {
      min: floorToQuarterHour(Math.min(...timestamps), timezone),
      max: ceilToQuarterHour(Math.max(...timestamps), timezone),
    };
  }, [tracks, timezone]);

  const isLive = scrubTime === null;

  const visibleTracks = tracks
    .filter((t) => !hiddenAssignmentIds.has(t.assignmentId))
    .map((track) => {
      const locations = isLive
        ? track.locations
        : track.locations.filter(
            (loc) => dayjs.utc(loc.recorded_at).valueOf() <= scrubTime,
          );
      return {
        assignmentId: track.assignmentId,
        locations,
        currentPoint: isLive
          ? track.latestLocation
          : (locations[locations.length - 1] ?? null),
      };
    });

  function formatTime(ms: number) {
    return dayjs(ms).tz(timezone).format("D MMM YYYY HH:mm");
  }

  return (
    <div>
      <div className="relative">
        <Map
          center={center}
          zoom={4}
          scrollWheelZoom={false}
          height={hasAnyLocation ? "360px" : "200px"}
        >
          <BasemapLayer />
          <FitBounds bounds={bounds} maxZoom={10} padding={mapPadding} />

          {departureCoords ? (
            <Marker
              position={departureCoords}
              icon={createCustomIcon("A", "#3b82f6")}
            />
          ) : null}

          {deliveryCoords ? (
            <Marker
              position={deliveryCoords}
              icon={createCustomIcon("B", "#10b981")}
            />
          ) : null}

          {visibleTracks.map(({ assignmentId, locations, currentPoint }) => {
            const color = colorByAssignment[assignmentId];
            const driverName = driverNameById[assignmentId];

            return (
              <Fragment key={assignmentId}>
                {locations.length > 1 ? (
                  <Polyline
                    positions={locations.map((loc) => [
                      loc.latitude,
                      loc.longitude,
                    ])}
                    pathOptions={{ color, weight: 4, opacity: 0.8 }}
                  />
                ) : null}
                {currentPoint ? (
                  <Marker
                    position={[currentPoint.latitude, currentPoint.longitude]}
                    icon={createAvatarIcon(
                      driverName,
                      color,
                      pictureUrlById[assignmentId],
                    )}
                  >
                    <Tooltip>{driverName ?? "Driver"}</Tooltip>
                  </Marker>
                ) : null}
              </Fragment>
            );
          })}
        </Map>

        {showEditButton && onEdit ? (
          <div className="absolute right-3 top-3 z-[999]">
            <Button plain onClick={onEdit}>
              <PencilIcon />
            </Button>
          </div>
        ) : null}
      </div>

      {timeBounds ? (
        <div className="grid gap-3 border-b px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {assignments.map((assignment) => {
              const hidden = hiddenAssignmentIds.has(assignment.id);
              return (
                <button
                  key={assignment.id}
                  type="button"
                  onClick={() => toggleAssignment(assignment.id)}
                  className={`flex items-center gap-2 rounded-full border py-0.5 pl-0.5 pr-3 text-left text-xs hover:bg-zinc-50 ${hidden ? "opacity-40" : ""}`}
                >
                  <Avatar
                    className="size-6 bg-gray-200 text-gray-600 outline-2 -outline-offset-2"
                    style={{ outlineColor: colorByAssignment[assignment.id] }}
                    src={pictureUrlById[assignment.id] ?? undefined}
                    initials={
                      pictureUrlById[assignment.id]
                        ? undefined
                        : getInitials(assignment.proDriver?.name ?? "?")
                    }
                  />
                  <span className="font-medium">
                    {assignment.proDriver?.name ?? "Driver"}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 text-xs">
            <span className="w-32 shrink-0 font-medium tabular-nums text-zinc-900">
              {formatTime(scrubTime ?? timeBounds.max)}
            </span>
            <input
              type="range"
              min={timeBounds.min}
              max={timeBounds.max}
              value={scrubTime ?? timeBounds.max}
              step={FIFTEEN_MINUTES}
              onChange={(e) => setScrubTime(Number(e.target.value))}
              className="min-w-0 flex-1 accent-zinc-700"
            />
            {isLive ? (
              <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                LIVE
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setScrubTime(null)}
                className="shrink-0 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-700 hover:bg-zinc-200"
              >
                Jump to live
              </button>
            )}
            <TimezoneSelect
              value={timezone}
              onChange={setTimezone}
              className="!w-auto shrink-0 [&_select]:!py-0.5 [&_select]:!pl-2 [&_select]:!pr-7 [&_select]:!text-xs/5"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
